export type ContentVariant = 'light' | 'dark';

/**
 * Parses common CSS color strings to RGB. Returns null if unsupported.
 */
function parseToRgb(color: string): { r: number; g: number; b: number } | null {
  const c = color.trim();
  if (!c) return null;

  const hexMatch = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(c);
  if (hexMatch) {
    const h = hexMatch[1];
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
      };
    }
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  const rgbMatch =
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/.exec(c);
  if (rgbMatch) {
    return {
      r: Math.min(255, Math.round(Number(rgbMatch[1]))),
      g: Math.min(255, Math.round(Number(rgbMatch[2]))),
      b: Math.min(255, Math.round(Number(rgbMatch[3]))),
    };
  }

  return null;
}

function relativeLuminance(r: number, g: number, b: number): number {
  const linearize = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const R = linearize(r);
  const G = linearize(g);
  const B = linearize(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Maps a resolved section background (hex/rgb) to card text variant:
 * - `'light'`: dark text (default for light surfaces)
 * - `'dark'`: light text for dark surfaces
 */
export function getContentVariantFromBackground(
  resolvedBackground: string | undefined | null,
): ContentVariant {
  if (!resolvedBackground?.trim()) {
    return 'light';
  }
  const rgb = parseToRgb(resolvedBackground);
  if (!rgb) {
    return 'light';
  }
  const l = relativeLuminance(rgb.r, rgb.g, rgb.b);
  return l < 0.45 ? 'dark' : 'light';
}

function contrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Returns whether text/background pair meets WCAG AA for normal text (4.5:1). */
export function hasAdequateContrast(
  textColor: string,
  backgroundColor: string,
  minRatio = 4.5,
): boolean {
  const textRgb = parseToRgb(textColor);
  const backgroundRgb = parseToRgb(backgroundColor);
  if (!textRgb || !backgroundRgb) {
    return true;
  }
  const textL = relativeLuminance(textRgb.r, textRgb.g, textRgb.b);
  const backgroundL = relativeLuminance(backgroundRgb.r, backgroundRgb.g, backgroundRgb.b);
  return contrastRatio(textL, backgroundL) >= minRatio;
}

/** Picks a fallback when CMS-configured text color fails contrast on the section background. */
export function pickReadableTextColor(
  textColor: string,
  backgroundColor: string | undefined | null,
  lightFallback: string,
  darkFallback: string,
): string {
  if (!backgroundColor?.trim()) {
    return textColor;
  }
  if (hasAdequateContrast(textColor, backgroundColor)) {
    return textColor;
  }
  return getContentVariantFromBackground(backgroundColor) === 'dark' ? darkFallback : lightFallback;
}
